import { createContext, useContext } from 'react';
import { generatePath } from 'react-router-dom';
import { BadgeLegacy } from '@accure/ui';
import { ChevronRight } from 'lucide-react';

import type { SitePreviewExtended } from '@/api/fe-api/domain/entities/site';
import { SiteOperationalPhase } from '@/api/fe-api/domain/entities/site-operational-phase';
import { PowerEnergy } from '@/components/sites/power-energy';
import { Link } from '@/components/ui/link';
import { Tooltip } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { cls } from '@/utils/cls';
import { siteLabel } from '@/utils/format/customer';
import { label } from '@/utils/format/label';

import type { SiteListItemProps } from '../entities/site-list';

import { SiteListItemRevenueOpportunity } from './site-list-revenue';

export const SiteListItemContext = createContext<
  SitePreviewExtended | undefined
>(undefined);

export function SiteListItem({ site }: SiteListItemProps) {
  const featureFlag = useFeatureFlag();
  const isMonitoringEnded =
    site.operationalPhase === SiteOperationalPhase.MONITORING_ENDED;

  return (
    <SiteListItemContext.Provider value={site}>
      <li
        key={site.id}
        className={cls('overflow-hidden rounded border', {
          'opacity-50': isMonitoringEnded,
        })}
        data-testid="asset-list-item"
      >
        <Link to={generatePath('/asset/:siteId', { siteId: site.id })}>
          <div className="flex gap-3 p-5 hover:bg-zinc-950/5">
            <div className="flex grow flex-col justify-between gap-2">
              <span className="font-semibold">
                {siteLabel(site.fullFrontendId)}
                {isMonitoringEnded ? (
                  <span className="ml-2 text-xs font-normal text-zinc-400">
                    Monitoring Ended
                  </span>
                ) : null}
              </span>
              <SiteListItemPowerEnergy />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {featureFlag('taskmanager') ? <SiteListItemTasks /> : null}
              {featureFlag('alerts') ? <SiteListItemAlerts /> : null}
            </div>
            <div className="flex items-center pl-2">
              <ChevronRight className="size-6 text-zinc-400" />
            </div>
          </div>
        </Link>
        {featureFlag('modoEnergyIntegration') && featureFlag('assets') ? (
          <SiteListItemRevenueOpportunity />
        ) : null}
      </li>
    </SiteListItemContext.Provider>
  );
}

function SiteListItemPowerEnergy() {
  const site = useContext(SiteListItemContext);

  if (!site) {
    return null;
  }

  return <PowerEnergy site={site} className="text-sm" />;
}

function SiteListItemAlerts() {
  const site = useContext(SiteListItemContext);

  if (!site?.alerts) {
    return null;
  }

  const { critical, warning } = site.alerts;

  if (critical > 0) {
    return (
      <Tooltip
        delay
        content={`${critical} ${label(['critical alert', 'critical alerts'], critical)}`}
      >
        <BadgeLegacy color="red">Critical</BadgeLegacy>
      </Tooltip>
    );
  }

  if (warning > 0) {
    return (
      <Tooltip
        delay
        content={`${warning} ${label(['warning alert', 'warning alerts'], warning)}`}
      >
        <BadgeLegacy color="yellow">Warning</BadgeLegacy>
      </Tooltip>
    );
  }

  return null;
}

function SiteListItemTasks() {
  const site = useContext(SiteListItemContext);

  if (!site?.nbOpenTasks) {
    return null;
  }

  return (
    <Tooltip
      delay
      content={`${site.nbOpenTasks} ${label(['Open Task', 'Open Tasks'], site.nbOpenTasks)}`}
    >
      <BadgeLegacy>Open Tasks</BadgeLegacy>
    </Tooltip>
  );
}
